import { BrandColors, Colors } from "@/constants/theme";
import FontAwesome5 from "@react-native-vector-icons/fontawesome5";
import React from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
  type ViewStyle,
} from "react-native";
import { ThemedText } from "../themed-text";
import { Label } from "./label";

interface BinaryChoiceProps {
  value: boolean | string | null | undefined;
  onValueChange: (value: any) => void;
  label?: string;
  options?: {
    trueLabel: string;
    falseLabel: string;
    trueDescription?: string;
    falseDescription?: string;
    notSureLabel?: string;
    notSureDescription?: string;
    trueValue: any;
    falseValue: any;
    notSureValue?: any;
  };
  showIcon?: boolean;
  style?: ViewStyle;
  optionsStyle?: ViewStyle;
  notSureOption?: boolean;
}

type ChoiceVariant = "primary" | "error";

const variantColors: Record<
  ChoiceVariant,
  { selectedBackground: string; selectedBorder: string; selectedAccent: string }
> = {
  primary: {
    selectedBackground: BrandColors.primaryLight,
    selectedBorder: BrandColors.primary,
    selectedAccent: BrandColors.primary,
  },
  error: {
    selectedBackground: BrandColors.errorLight,
    selectedBorder: BrandColors.error,
    selectedAccent: BrandColors.error,
  },
};

function RadioIndicator({
  selected,
  accentColor,
}: {
  selected: boolean;
  accentColor: string;
}) {
  if (selected) {
    return (
      <FontAwesome5
        name="dot-circle"
        size={18}
        color={accentColor}
        iconStyle="solid"
      />
    );
  }

  return <View style={styles.radioOutline} />;
}

function ChoiceCard({
  selected,
  title,
  description,
  showIcon,
  onPress,
  backgroundColor,
  borderColor,
  variant = "primary",
}: {
  selected: boolean;
  title: string;
  description?: string;
  showIcon: boolean;
  onPress: () => void;
  backgroundColor: string;
  borderColor: string;
  variant?: ChoiceVariant;
}) {
  const hasDescription = description != null && description !== "";
  const colors = variantColors[variant];
  const accentColor = selected ? colors.selectedAccent : BrandColors.white;

  const cardStyle = [
    hasDescription ? styles.optionButtonCard : styles.optionButtonCompact,
    {
      backgroundColor: selected ? colors.selectedBackground : backgroundColor,
      borderColor: selected ? colors.selectedBorder : borderColor,
    },
  ];

  const cardDescriptionStyle = [
    styles.optionButtonCard,
    {
      backgroundColor: selected
        ? variantColors["primary"].selectedBackground
        : backgroundColor,
      borderColor: selected
        ? variantColors["primary"].selectedBorder
        : borderColor,
    },
  ];

  if (!hasDescription) {
    return (
      <TouchableOpacity style={cardStyle} onPress={onPress} activeOpacity={0.8}>
        <ThemedText
          style={[styles.compactTitle, selected && { color: accentColor }]}
          type="smallSemiBold"
        >
          {title}
        </ThemedText>
        {showIcon ? (
          <View style={styles.radioContainer}>
            <RadioIndicator
              selected={selected}
              accentColor={colors.selectedAccent}
            />
          </View>
        ) : null}
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity
      style={cardDescriptionStyle}
      onPress={onPress}
      activeOpacity={0.8}
    >
      <ThemedText
        style={[styles.optionTitle, selected && { color: BrandColors.primary }]}
        type="smallSemiBold"
      >
        {title}
      </ThemedText>
      <ThemedText style={styles.optionDescription} type="extraSmall">
        {description}
      </ThemedText>
    </TouchableOpacity>
  );
}

export function BinaryChoice({
  value,
  onValueChange,
  label,
  options = {
    trueLabel: "Yes",
    falseLabel: "No",
    notSureLabel: "Not Sure",
    trueValue: true,
    falseValue: false,
    notSureValue: undefined,
  },
  showIcon = true,
  style,
  optionsStyle,
  notSureOption = false,
}: BinaryChoiceProps) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];

  const isTrueSelected = value === options.trueValue;
  const isFalseSelected = value === options.falseValue;
  const isNotSureSelected = value === options.notSureValue;

  return (
    <View style={[styles.container, style]}>
      {label ? <Label label={label} /> : null}
      <View style={[styles.optionsContainer, optionsStyle]}>
        <ChoiceCard
          selected={isTrueSelected}
          title={options.trueLabel}
          description={options.trueDescription}
          showIcon={showIcon}
          onPress={() => onValueChange(options.trueValue)}
          backgroundColor={theme.background}
          borderColor={theme.border}
        />
        <ChoiceCard
          selected={isFalseSelected}
          title={options.falseLabel}
          description={options.falseDescription}
          showIcon={showIcon}
          onPress={() => onValueChange(options.falseValue)}
          backgroundColor={theme.background}
          borderColor={theme.border}
          variant="error"
        />
        {notSureOption && (
          <ChoiceCard
            selected={isNotSureSelected}
            title={options.notSureLabel ?? "Not Sure"}
            description={options.notSureDescription}
            showIcon={showIcon}
            onPress={() => onValueChange(options.notSureValue)}
            backgroundColor={theme.background}
            borderColor={theme.border}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  optionsContainer: {
    flexDirection: "row",
    gap: 12,
  },
  optionButtonCompact: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 14,
    borderWidth: 1,
    minHeight: 52,
    position: "relative",
  },
  optionButtonCard: {
    flex: 1,
    alignItems: "flex-start",
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: 1,
    gap: 4,
    minHeight: 72,
  },
  compactTitle: {
    flex: 1,
    fontSize: 15,
    textAlign: "center",
    paddingRight: 20,
  },
  radioContainer: {
    position: "absolute",
    right: 16,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  radioOutline: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1.5,
    borderColor: BrandColors.lightGray,
  },
  optionTitle: {
    fontSize: 15,
    lineHeight: 20,
  },
  optionDescription: {
    fontSize: 11.5,
    color: BrandColors.mediumGray,
    lineHeight: 16,
  },
});
